<script setup lang="ts">

import {PropType, Ref, ref} from "vue";
import {CustomDTOExpression} from "@/utils/presenters/interfaces.ts";
import Translator from "@/utils/common/Translator.ts";
import DropDownFieldWidgetUtil, {DisplayedAllowedValue} from "@/utils/editView/widgets/DropDownFieldWidgetUtil.ts";
import {QSelect} from "quasar";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import CriteriaField = namaTypes.CriteriaField;

interface CustomCriteriaField extends CriteriaField
{
  label?: string,
  searchValue?: string
}

const model = defineModel({
  type: Object as PropType<CustomDTOExpression>
});
const emit = defineEmits(["afterUpdateCriteriaField"]);
const props = defineProps({
  criteriaFields: {
    type: Object as PropType<CriteriaField[]>,
    required: true
  }, ownerEntity: {
    type: String,
    required: true,
  }
});
const criteriaFields = props.criteriaFields as CustomCriteriaField[];
const lhsOptions: Ref<CustomCriteriaField[]> = ref(criteriaFields ?? []);
lhsOptions.value.forEach(l => {
  l.label = Translator.translateTitledIDWithPrefixes(l, props.ownerEntity!);
  l.searchValue = EditScreenFieldUtils.fetchSearchTextForAllowedValue(l.id!, props.ownerEntity);
});
const selectedField: Ref<CustomCriteriaField | null | undefined> = ref(fetchSelectedField());
const inputValue = ref<string | null | undefined>(selectedField.value?.label);

function fetchSelectedField() {
  const field = model.value!.field;
  let value: CustomCriteriaField | undefined;
  if (field)
    value = lhsOptions.value.find(l => l.id === field);
  return value;
}

function onSelect(newValue: CustomCriteriaField) {
  if (selectedField.value && selectedField.value?.id === newValue.id)
    return;
  selectedField.value = newValue;
  model.value!.field = newValue ? newValue.id : undefined;
  emit('afterUpdateCriteriaField');
}

function dropDrownFilter(val: any, update: any) {
  update(() => filter(val),
    ref => {
      if (val !== '' && ref.options.length > 0) {
        ref.setOptionIndex(-1);
        ref.moveOptionSelection(1, true);
      }
    })
}

function filter(value: string | null | undefined) {
  if (!value) {
    lhsOptions.value = criteriaFields;
    return;
  }
  lhsOptions.value = criteriaFields.filter(e => DropDownFieldWidgetUtil.isAllowedValueMatched(e as DisplayedAllowedValue, value))
    .sort((a, b) => a.id === b.id ? 0 : a.searchValue!.length - b.searchValue!.length);
}

function onPartialUserInput(val) {
  if (!val) {
    selectedField.value = null;
    model.value!.field = undefined;
    emit('afterUpdateCriteriaField');
  }
  inputValue.value = val;
}

function onUserInputFinished() {
  if (inputValue.value) {
    const matched: CustomCriteriaField | undefined = criteriaFields.find(e => e.label === inputValue.value);
    if (matched) {
      if (selectedField.value && selectedField.value.id === matched.id)
        return;
      selectedField.value = matched;
      inputValue.value = matched.label;
      model.value!.field = matched.id;
      emit('afterUpdateCriteriaField');
    } else {
      if (!selectedField.value)
        return;
      selectedField.value = null;
      model.value!.field = undefined;
      emit('afterUpdateCriteriaField');
    }
  } else if (selectedField.value) {
    selectedField.value = null;
    model.value!.field = undefined;
    emit('afterUpdateCriteriaField');
  }
}

const select = ref<QSelect>()

function focus() {
  if (select.value)
    select.value.focus();
}

defineExpose({focus})
</script>

<template>
  <q-select dense options-dense outlined use-input hide-selected fill-input
            :model-value="selectedField" ref="select"
            :options="lhsOptions"
            @update:model-value="onSelect"
            @filter="dropDrownFilter"
            @input-value="onPartialUserInput"
            @blur="onUserInputFinished"
            option-label="label"
            option-value="id"
            input-debounce="0"
            class="nama-field-input nama-customize-dense-input"
            input-class="tw-text-base tw-font-normal dark:tw-text-namaFontColor-input-dark tw-bg-transparent focus:tw-ring-0"
            options-selected-class="tw-pl-[6px] tw-pr-[3px] tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-input-dark"
            popup-content-class="!tw-max-h-[30vh] tw-text-base tw-font-normal dark:tw-text-namaFontColor-input-dark dark:tw-bg-namaBackground-bright-dark"/>
</template>

<style scoped>

</style>
